import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  InputAdornment,
  CardActions,
  TableSortLabel,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import { useAuthContext } from '../../../../hooks/useAuthContext';
import axios from '../../../../services/ApiService';
import { paths } from '../../../../utils/paths';
import LoadingSpinner from '../../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProgramSubscription, ProgramData } from '../../../types/ProgramTypes';
import { BVTConstants } from '../../../../utils/BVTConstants';

type Order = 'asc' | 'desc';
type OrderBy = 'startDate' | 'endDate' | 'programName' | 'amount';

function CompanySubscriptions() {
  const [loading, setLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState<ProgramSubscription[]>([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState<ProgramSubscription[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('startDate');
  const [programs, setPrograms] = useState<ProgramData[]>([]);
  const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<ProgramData | null>(null);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [programsLoading, setProgramsLoading] = useState(false);
  const location = useLocation();
  const companyId = (location.state as { companyId?: number })?.companyId;
  const navigate = useNavigate();
  const { user } = useAuthContext();

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  useEffect(() => {
    filterAndSortSubscriptions();
  }, [subscriptions, searchTerm, order, orderBy]);

  const fetchPrograms = async (): Promise<boolean> => {
    setProgramsLoading(true);
    try {
      const response = await axios.get('/getProgramsMapForSubscription');
      if (Array.isArray(response.data)) {
        setPrograms(response.data);
      }
      return true;
    } catch (error) {
      console.error('Error fetching programs:', error);
      //toast.error('Failed to fetch programs');
      setPrograms([]);
      return false;
    } finally {
      setProgramsLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      let companyIdToUse = companyId || 0;
      const response = await axios.post('/getProgramSubscriptions', companyIdToUse);
      if (Array.isArray(response.data)) {
        setSubscriptions(response.data);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      //toast.error('Failed to fetch subscriptions');
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortSubscriptions = () => {
    // Ensure subscriptions is an array before filtering
    if (!Array.isArray(subscriptions)) {
      setFilteredSubscriptions([]);
      return;
    }

    let filtered = subscriptions.filter(subscription =>
      subscription.companyName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (orderBy) {
        case 'startDate':
          aValue = new Date(a.startDate).getTime();
          bValue = new Date(b.startDate).getTime();
          break;
        case 'endDate':
          aValue = new Date(a.endDate).getTime();
          bValue = new Date(b.endDate).getTime();
          break;
        case 'programName':
          aValue = a.companyName;
          bValue = b.companyName;
          break;
        case 'amount':
          aValue = a.amount;
          bValue = b.amount;
          break;
        default:
          aValue = a.companyName;
          bValue = b.companyName;
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredSubscriptions(filtered);
  };

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSubscribe = async () => {
    const success = await fetchPrograms();
    if (success) {
      setSubscribeDialogOpen(true);
    }
  };

  const handleCloseSubscribeDialog = () => {
    setSubscribeDialogOpen(false);
    setSelectedProgram(null);
  };

  const handleProgramSelect = (event: any) => {
    const programId = event.target.value;
    const program = programs.find(p => p.id === programId);
    setSelectedProgram(program || null);
  };

  const handleSubscribeProgram = async () => {
    if (!selectedProgram || !user) return;

    setSubscribeLoading(true);
    try {
      const companyData = await axios.get("/getCurrentUserCompany");
      const subscriptionData: ProgramSubscription = {
        subId: 0,
        companyId: companyData.data.companyId,
        programId: selectedProgram.id,
        companyName: companyData.data.companyName,
        programName: selectedProgram.programName,
        amount: selectedProgram.amount,
        currency: selectedProgram.currency,
        frequency: selectedProgram.frequency,
        startDate: '',
        endDate: '',
        status: 'ACTIVE',
        payments: []
      };

      const response = await axios.post('/subscribeProgram', subscriptionData);
      toast.success('Successfully subscribed to program');
      fetchSubscriptions(); // Refresh the subscriptions list
    } catch (error) {
      console.error('Error subscribing to program:', error);
      toast.error('Failed to subscribe to program');
    } finally {
      setSubscribeLoading(false);
      handleCloseSubscribeDialog();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'error';
      case 'pending':
      case 'overdue':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    if(dateString === 'NA'){
      return 'NA';
    }
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner loading={loading} />
      ) : (
        <Card>
          <CardHeader 
            subheader="Program Subscriptions" 
            title="Subscriptions"
            action={
              user?.role === BVTConstants.USER_TYPE_COMPANY && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubscribe}
                  disabled={programsLoading}
                >
                  {programsLoading ? 'Loading...' : 'Subscribe'}
                </Button>
              )
            }
          />
          <Divider />
          <CardContent>
            <Stack spacing={3}>
              <TextField
                fullWidth
                placeholder="Search by program name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'programName'}
                          direction={orderBy === 'programName' ? order : 'asc'}
                          onClick={() => handleRequestSort('programName')}
                        >
                          Program Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'amount'}
                          direction={orderBy === 'amount' ? order : 'asc'}
                          onClick={() => handleRequestSort('amount')}
                        >
                          Amount
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Frequency</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'startDate'}
                          direction={orderBy === 'startDate' ? order : 'asc'}
                          onClick={() => handleRequestSort('startDate')}
                        >
                          Start Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'endDate'}
                          direction={orderBy === 'endDate' ? order : 'asc'}
                          onClick={() => handleRequestSort('endDate')}
                        >
                          End Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSubscriptions.map((subscription) => (
                      <TableRow 
                        key={subscription.subId}
                        onClick={() => navigate(paths.agent.companySubscriptionDetails, { state: { subscriptionId: subscription.subId } })}
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                      >
                        <TableCell>{subscription.programName}</TableCell>
                        <TableCell>
                          {subscription.amount.toFixed(2)}
                        </TableCell>
                        <TableCell>{subscription.frequency}</TableCell>
                        <TableCell>{formatDate(subscription.startDate)}</TableCell>
                        <TableCell>{formatDate(subscription.endDate || 'NA')}</TableCell>
                        <TableCell>
                          <Chip
                            label={subscription.status}
                            color={getStatusColor(subscription.status) as any}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredSubscriptions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No subscriptions found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </CardContent>
        </Card>
      )}
      <Dialog open={subscribeDialogOpen} onClose={handleCloseSubscribeDialog}>
        <DialogTitle>Select Program</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ minWidth: 400, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="program-select-label">Program</InputLabel>
              <Select
                labelId="program-select-label"
                id="program-select"
                value={selectedProgram?.id || ''}
                label="Program"
                onChange={handleProgramSelect}
              >
                {programs.map((program) => (
                  <MenuItem key={program.id} value={program.id}>
                    {program.programName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                {selectedProgram?.description}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body1">
                    <strong>Amount:</strong> {selectedProgram?.amount?.toFixed(2) || "NA"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1">
                    <strong>Frequency:</strong> {selectedProgram?.frequency || "NA"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubscribeDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleSubscribeProgram} 
            color="primary" 
            variant="contained"
            disabled={!selectedProgram || subscribeLoading}
          >
            {subscribeLoading ? 'Subscribing...' : 'Subscribe'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CompanySubscriptions;